// @ts-strict-ignore
import { Action, ActionNavContext, parseTokens } from '@coast/schemas';
import { mapValues } from 'lodash';
import React from 'react';
import { FormContext } from '@shared/components/blocks/contexts/form';
import { ToastAPI } from '@shared/components/toast';
import { filterBySlug, resolveTemplate } from '@shared/scenes/cards/hooks';
import { CardTemplate } from '@shared/scenes/cards/types';
import { getStore } from '@shared/store';
import { MBActionSheet } from '@shared/util/actionSheet';
import { MBLinking } from '@shared/util/linking';
import { Navigator } from '@shared/util/navigation';
import { MBSentry } from '@shared/util/sentry';

function parseFieldOverrideTokens(fieldOverrides: { [key: string]: any }) {
  const store = getStore();
  return mapValues(fieldOverrides, value =>
    parseTokens(value, { resolveCurrentUserId: () => store.getState().appState.currentUserId }),
  );
}

function navigate(
  cardId: string,
  channelSlug: string,
  context: ActionNavContext['context'],
  navigator: Navigator,
  workflowTemplateId?: string,
  templateSlug?: string,
  fieldOverrides?: { [key: string]: any },
): void {
  switch (context) {
    case 'create':
      navigator.navigate('CardCreate', { channelSlug, templateSlug, fieldOverrides });
      break;
    case 'edit':
      navigator.navigate('CardUpdate', {
        cardId,
        channelSlug,
        templateSlug,
        fieldOverrides,
        workflowTemplateId,
      });
      break;
    case 'list':
    case 'agenda':
    case 'kanban':
      navigator.navigate('Channel', { channelSlug });
      navigator.navigate('ChannelCardList', { channelSlug });
      break;
    case 'detail':
      navigator.navigate('CardDetails', { cardId, channelSlug, templateSlug });
      break;
    default:
      MBSentry.breadcrumb({
        category: 'cards',
        message: 'invalid context while evaluating navigate action',
        data: {
          cardId,
          channelSlug,
          context,
          templateSlug,
        },
      });
      break;
  }
}

async function executeAction(
  action: Action,
  card: MBAPI.Card,
  channelSlug: string,
  formContext: FormContext | null,
  toast: ToastAPI,
  templates: CardTemplate[],
  navigator: Navigator,
  node?: React.Component,
) {
  const store = getStore();
  switch (action.type) {
    case 'actionUrl':
      if (!!action.name) {
        MBLinking.open(formContext.values[action.name]);
      } else {
        MBLinking.open(action.url);
      }
      break;
    case 'actionSheet':
      MBActionSheet.show({
        parent: node,
        options: action.options.map(option => ({
          title: option.label,
          onPress: () =>
            executeActions(
              option.actions,
              card,
              channelSlug,
              formContext,
              toast,
              templates,
              navigator,
              node,
            ),
        })),
      });
      break;
    case 'actionSetValues':
      const prevValues = { ...formContext.values };
      action.values.forEach(({ name, value }) => {
        const parsedValue = parseTokens(value, {
          resolveCurrentUserId: () => store.getState().appState.currentUserId,
          resolveCard: () => prevValues,
        });
        formContext.setFieldValue(name, parsedValue);
      });
      break;
    case 'actionSubmitForm':
      await formContext.submitForm();
      break;
    case 'actionNavContext': {
      const fieldOverrides = !!action.fieldOverrides
        ? parseFieldOverrideTokens(action.fieldOverrides)
        : undefined;
      navigate(
        card.id,
        channelSlug,
        action.context,
        navigator,
        undefined,
        undefined,
        fieldOverrides,
      );
      break;
    }
    case 'actionNavTemplate': {
      let template = resolveTemplate(templates, [filterBySlug(action.templateSlug)]);
      if (!!template && template.context !== 'persistent-menu') {
        const fieldOverrides = !!action.fieldOverrides
          ? parseFieldOverrideTokens(action.fieldOverrides)
          : undefined;
        navigate(
          card.id,
          channelSlug,
          template.context as ActionNavContext['context'],
          navigator,
          undefined,
          action.templateSlug,
          fieldOverrides,
        );
      }
      break;
    }
    case 'actionToast':
      toast.show({
        message: action.toastOptions.message,
        style: action.toastOptions.style,
        icon: action.toastOptions.icon,
        actions: action.toastOptions.buttons?.map(button => ({
          label: button.label,
          onPress: () =>
            executeActions(
              button.actions,
              card,
              channelSlug,
              formContext,
              toast,
              templates,
              navigator,
              node,
            ),
        })),
      });
      break;
    default:
      MBSentry.breadcrumb({
        category: 'cards',
        message: 'invalid action type while executing action',
        data: {
          card,
          channelSlug,
          action,
        },
      });
      break;
  }
}

export async function executeActions(
  actions: Action[],
  card: MBAPI.Card,
  channelSlug: string,
  formContext: FormContext | null,
  toast: ToastAPI,
  templates: CardTemplate[],
  navigator: Navigator,
  node: React.Component,
) {
  for (let action of actions) {
    await executeAction(action, card, channelSlug, formContext, toast, templates, navigator, node);
  }
}
